import { Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';

interface ArticleLayoutProps {
  title: string;
  description: string;
  children: React.ReactNode;
  schemaData?: Record<string, unknown>;
  headerImage?: string;
  headerTag?: string;
}

export default function ArticleLayout({ title, description, children, schemaData, headerImage, headerTag }: ArticleLayoutProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.living';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const enrichedSchema = useMemo(() => {
    if (!schemaData) return null;
    return {
      ...schemaData,
      url: `${siteUrl}${location.pathname}`,
      description,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}${location.pathname}`,
      },
    };
  }, [schemaData, description, location.pathname, siteUrl]);

  return (
    <main>
      <nav className="fixed top-0 left-0 w-full z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="text-lg font-semibold text-foreground-900 font-heading tracking-tight">
              pandabuy<span className="text-primary-500">.living</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              <span>Home</span>
            </Link>
            <Link
              to="/pandabuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-sm font-medium hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              <span>Shopping Guide</span>
            </Link>
          </div>
        </div>
      </nav>

      <article className="pt-24 pb-20">
        <header className="max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-block px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase">
              {headerTag || 'PandabuySpreadsheet'}
            </span>
            <span className="text-xs text-foreground-400">Updated June 2026</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-6 text-balance">
            {title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
            {description}
          </p>
          {headerImage && (
            <div className="rounded-2xl overflow-hidden mt-8">
              <img
                src={headerImage}
                alt={title}
                title={title}
                className="w-full h-64 md:h-80 object-cover rounded-2xl object-top"
                loading="lazy"
              />
            </div>
          )}
        </header>

        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="prose prose-lg max-w-none text-foreground-700 space-y-8">
            {children}

            <div className="bg-accent-100/80 rounded-2xl p-8 md:p-10 border border-accent-200/60 text-center mt-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                Ready to Start Exploring?
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto mb-6 leading-relaxed">
                Dive into the most complete pandabuyspreadsheet collection available. Thousands of verified items across 14 categories await.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Pandabuy Sheet
                </a>
                <Link
                  to="/"
                  className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-8 mt-12">
              <p className="text-xs text-foreground-400 mb-3">
                Related: Explore more guides on our{' '}
                <Link to="/" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">homepage</Link>{' '}
                or read the{' '}
                <Link to="/pandabuy-spreadsheet-guide" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">complete pandabuyspreadsheet guide</Link>.
              </p>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-accent-900 text-background-50/80 py-10 px-4 md:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-sm"></i>
            </div>
            <span className="text-sm font-semibold text-background-50 font-heading">
              pandabuy<span className="text-accent-400">.living</span>
            </span>
          </Link>
          <p className="text-xs text-background-50/30">
            &copy; {new Date().getFullYear()} pandabuy.living &mdash; <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">Back to Homepage</Link>
          </p>
        </div>
      </footer>

      {enrichedSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(enrichedSchema) }}
        />
      )}
    </main>
  );
}